import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import { useWorkspace } from '../context/WorkspaceContext'
import { useQuickStart } from '../context/QuickStartContext'
import { ensureUserAccount } from '../services/accountService'
import Navbar from '../components/Navbar'
import Chatbot from '../components/Chatbot'
import OrgSwitcher from '../components/OrgSwitcher'
import { supabase } from '../services/supabaseClient'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'

// Import dashboard components
import {
  KeyMetricsCards,
  QuickWinsWidget,
  IssueDistributionChart,
  BurnDownChart,
  FlowErrorTrendChart,
  ErrorTypeDistribution,
  AffectedUsersWidget,
  MostAffectedUsersEnhanced,
  FlowsByErrorType,
  ErrorTypesList,
  type QuickWin,
  type IssueDistribution,
  type FlowErrorData,
  type ErrorDistribution,
  type AffectedUser
} from '../components/dashboard'
import CriticalIssuesWidget from '../components/dashboard/CriticalIssuesWidget'

interface DashboardMetrics {
  healthGrade: string;
  averageScore: number;
  criticalIssues: number;
  incompleteFlows: number;
  weeklyFixed: number;
  weeklyTrend: 'up' | 'down' | 'stable';
  totalFlows: number;
  issuesByType: { [key: string]: number };
  lastSync: string;
}

interface BurnUpData {
  chartData: Array<{ date: string; cumulativeFixed: number }>;
  summary: {
    thisWeek: number;
    thisMonth: number;
    allTime: number;
  };
}

// Types are now imported from components

// Constants moved to components

export default function Dashboard() {
  const { user, signOut, supabase } = useAuth()
  const { currentOrgId, setCurrentOrgId } = useWorkspace()
  const { markStepComplete, stepsCompleted, resetQuickStart } = useQuickStart()
  const { orgId: urlOrgId } = useParams<{ orgId: string }>()
  const [shouldLoadSalesforce, setShouldLoadSalesforce] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<{type: 'success' | 'error' | 'none', message: string | React.ReactNode}>({
    type: 'none',
    message: ''
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Check if in demo mode
  const isDemoMode = searchParams.get('mode') === 'demo'
  
  // Dashboard metrics state
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [burnUpData, setBurnUpData] = useState<BurnUpData | null>(null)
  const [issueDistribution, setIssueDistribution] = useState<IssueDistribution[]>([])
  const [quickWins, setQuickWins] = useState<QuickWin[]>([])
  // State moved to individual components
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const [isSyncingInterviews, setIsSyncingInterviews] = useState(false)
  const [issueHistory, setIssueHistory] = useState<any>(null)
  
  // Flow error states
  const [flowErrorData, setFlowErrorData] = useState<FlowErrorData | null>(null)
  const [errorDistribution, setErrorDistribution] = useState<ErrorDistribution[]>([])
  const [affectedUsers, setAffectedUsers] = useState<AffectedUser[]>([])
  const [criticalIssues, setCriticalIssues] = useState<any[]>([])
  
  // UI expansion states
  // State moved to individual components
  
  // Ref for Most Affected Users section
  const mostAffectedUsersRef = useRef<HTMLDivElement>(null);
  const mostAffectedUsersComponentRef = useRef<any>(null);
  const [shouldExpandAffectedUsers, setShouldExpandAffectedUsers] = useState(false);
  const [expandedWidget, setExpandedWidget] = useState<string | null>(null);
  
  // Use a separate state to track whether we've attempted to load Salesforce data
  const [hasFetchedSalesforce, setHasFetchedSalesforce] = useState(false);

  // Check for resetQuickStart query parameter
  useEffect(() => {
    if (searchParams.get('resetQuickStart') === 'true') {
      // Set flag to prevent auto-checking on this page load
      sessionStorage.setItem('quickStartJustReset', 'true')
      resetQuickStart()
      // Remove the parameter from URL
      searchParams.delete('resetQuickStart')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, resetQuickStart])

  // No need to reset - let the component manage its own state after initial expansion

  // Always call the hook but with skipInitialFetch
  const { orgs, isLoading: isLoadingOrgs, error: salesforceError, userRole, refetch } = useSalesforce({
    skipInitialFetch: true
  })
  
  // Only trigger a Salesforce fetch once when shouldLoadSalesforce becomes true
  useEffect(() => {
    if (shouldLoadSalesforce && !hasFetchedSalesforce && refetch) {
      setHasFetchedSalesforce(true);
      refetch().catch(err => {
        // Error is handled by the query's error state
      });
    }
  }, [shouldLoadSalesforce, hasFetchedSalesforce, refetch]);
  
  
  // Force session check on dashboard load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          // Session errors are handled in the auth context
        } else {
          if (data.session?.user) {
            
            // Check user's accounts
            try {
              // First try to find accounts where the user is the owner
              const { data: ownedAccounts, error: ownedError } = await supabase
                .from('accounts')
                .select('id, name')
                .eq('owner_id', data.session.user.id);
              
              if (ownedError) {
                // Account errors will be handled below
              } else {
                
                // Set the shouldLoadSalesforce flag if we found any owned accounts
                if (ownedAccounts && ownedAccounts.length > 0) {
                  setShouldLoadSalesforce(true);
                }
              }
              
              // Check for accounts where the user has a role assigned
              const { data: roleAccounts, error: roleError } = await supabase
                .from('user_account_roles')
                .select('account_id')
                .eq('user_id', data.session.user.id);
              
              if (roleError) {
                // Role account errors are non-critical
              } else {
                
                // Set the shouldLoadSalesforce flag if we found any accounts
                if (roleAccounts && roleAccounts.length > 0) {
                  setShouldLoadSalesforce(true);
                }
              }
              
              // If no accounts found, try to set up an account for the user
              if ((!ownedAccounts || ownedAccounts.length === 0) && (!roleAccounts || roleAccounts.length === 0)) {
                // No accounts found for user - attempting account creation
                try {
                  // Use our direct account service instead of calling Edge Function
                  // This avoids CORS issues in production
                  const success = await ensureUserAccount(data.session);
                  
                  if (success) {
                    
                    // Set shouldLoadSalesforce flag to trigger Salesforce loading
                    setShouldLoadSalesforce(true);
                  } else {
                    // Account creation failed via account service
                    
                    // Try one more method as a last resort - direct database operations through the service
                    const fullName = data.session.user.user_metadata?.full_name || 
                                    data.session.user.user_metadata?.name || 
                                    data.session.user.email?.split('@')[0] || 
                                    'Dashboard User';
                    
                    const accountName = `${fullName}'s Emergency Account (${new Date().toISOString()})`;
                    
                    try {
                      const result = await supabase
                        .from('accounts')
                        .insert({
                          name: accountName,
                          owner_id: data.session.user.id,
                          subscription_tier: 'free',
                          subscription_status: 'active'
                        })
                        .select()
                        .single();
                        
                      if (result.error) {
                        // Last resort account creation failed
                      } else if (result.data) {
                        
                        // Try to get Super-Admin role ID
                        const roleResult = await supabase
                          .from('roles')
                          .select('id')
                          .eq('name', 'Super-Admin')
                          .single();
                          
                        if (roleResult.error) {
                          // Error getting Super-Admin role
                        } else if (roleResult.data) {
                          // Assign role
                          const roleAssign = await supabase
                            .from('user_account_roles')
                            .insert({
                              user_id: data.session.user.id,
                              account_id: result.data.id,
                              role_id: roleResult.data.id
                            });
                            
                          if (roleAssign.error) {
                            // Error assigning role
                          } else {
                            // Set shouldLoadSalesforce flag to trigger Salesforce loading
                            setShouldLoadSalesforce(true);
                          }
                        }
                      }
                    } catch (lastResortError) {
                      // Last resort account creation exception
                    }
                  }
                } catch (accountError) {
                  // Exception during account creation
                }
              }
            } catch (accountCheckError) {
              // Error checking user accounts
            }
          }
        }
      } catch (err) {
        // Session check errors are handled by auth context
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkSession();
  }, [supabase.auth]);
  
  // Handle org context from URL
  useEffect(() => {
    // If we have an orgId in the URL, use it
    if (urlOrgId) {
      setSelectedOrgId(urlOrgId);
      setCurrentOrgId(urlOrgId);
    } 
    // If no URL orgId but we have orgs, redirect to first org
    else if (orgs.length > 0 && !urlOrgId) {
      const firstOrgId = orgs[0].id;
      navigate(`/${firstOrgId}/dashboard`, { replace: true });
    }
  }, [urlOrgId, orgs, navigate, setCurrentOrgId]);

  // Fetch dashboard data when org is selected
  useEffect(() => {
    if (selectedOrgId) {
      fetchDashboardData();
      // Don't automatically sync flow interviews - let user trigger it manually
      // This avoids INVALID_SESSION errors when Salesforce tokens have expired
    }
  }, [selectedOrgId]);

  const fetchDashboardData = async () => {
    if (!selectedOrgId) return;
    
    setDashboardLoading(true);
    setDashboardError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = {
        'Authorization': session ? `Bearer ${session.access_token}` : ''
      };

      // Fetch all dashboard data in parallel
      const [metricsRes, burnUpRes, issuesRes, quickWinsRes, flowErrorsRes, errorDistRes, affectedUsersRes, issueHistoryRes, criticalIssuesRes] = await Promise.all([
        fetch(`/api/dashboard/metrics?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/burn-up?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/issues?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/quick-wins?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/flow-errors?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/error-distribution?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/affected-users?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/issue-history?orgId=${selectedOrgId}`, { headers: authHeaders }),
        fetch(`/api/dashboard/critical-issues?orgId=${selectedOrgId}`, { headers: authHeaders })
      ]);

      if (!metricsRes.ok || !burnUpRes.ok || !issuesRes.ok || !quickWinsRes.ok || 
          !flowErrorsRes.ok || !errorDistRes.ok || !affectedUsersRes.ok || !issueHistoryRes.ok || !criticalIssuesRes.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const [metricsData, burnUpData, issuesData, quickWinsData, flowErrorsData, errorDistData, affectedUsersData, issueHistoryData, criticalIssuesData] = await Promise.all([
        metricsRes.json(),
        burnUpRes.json(),
        issuesRes.json(),
        quickWinsRes.json(),
        flowErrorsRes.json(),
        errorDistRes.json(),
        affectedUsersRes.json(),
        issueHistoryRes.json(),
        criticalIssuesRes.json()
      ]);

      // Override values for demo mode
      if (isDemoMode) {
        setMetrics({
          ...metricsData,
          healthGrade: 'D+',
          criticalIssues: 27
        });
        // Set affected users count to 11
        setAffectedUsers(affectedUsersData.users?.slice(0, 11) || []);
      } else {
        setMetrics(metricsData);
        setAffectedUsers(affectedUsersData.users || []);
      }
      
      setBurnUpData(burnUpData);
      setIssueDistribution(issuesData.issues || []);
      setQuickWins(quickWinsData.quickWins || []);
      setFlowErrorData(flowErrorsData);
      setErrorDistribution(errorDistData.distribution || []);
      setIssueHistory(issueHistoryData);
      setCriticalIssues(criticalIssuesData.criticalIssues || []);
    } catch (err) {
      setDashboardError('Failed to load dashboard data. Please try again.');
    } finally {
      setDashboardLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper functions moved to components

  const syncFlowInterviews = async () => {
    if (!selectedOrgId) return;
    
    setIsSyncingInterviews(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Get fresh tokens from client
      const tokens = await getAuthToken(selectedOrgId);
      
      const endpoint = '/api/salesforce/sync-flow-interviews';
      let response, error;
      
      if (isEdgeFunctionMigrated(endpoint)) {
        // Use Edge Function
        const functionName = getEdgeFunctionName(endpoint);
        try {
          const result = await callEdgeFunction(functionName!, {
            orgId: selectedOrgId
          });
          response = { ok: true };
          error = null;
        } catch (e) {
          response = { ok: false, status: 500 };
          error = { message: e.message };
        }
      } else {
        // Fallback to Vercel API
        const httpResponse = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': session ? `Bearer ${session.access_token}` : ''
          },
          body: JSON.stringify({ 
            orgId: selectedOrgId,
            access_token: tokens.access_token,
            instance_url: tokens.instance_url
          })
        });
        
        response = httpResponse;
        if (!httpResponse.ok) {
          error = await httpResponse.json();
        }
      }

      if (!response.ok) {
        
        // Check if we need to reauthorize
        if (response.status === 401 && error.requiresReauth) {
          setConnectionStatus({
            type: 'error',
            message: 'Salesforce authentication expired. Please reconnect your org.'
          });
          
          // Optionally, you could redirect to the org settings page
          // navigate(`/${orgId}/settings`);
          
          return;
        }
        
        // Log full error details for debugging
        console.error('Sync error details:', error);
        
        throw new Error(error.details || error.error || 'Failed to sync flow interviews');
      }

      const result = await response.json();
      
      // Show success message
      setConnectionStatus({
        type: 'success',
        message: `Successfully synced ${result.synced} flow interview errors`
      });
      
      // Refresh dashboard data to show the new errors
      await fetchDashboardData();
    } catch (error: any) {
      
      // Handle INVALID_SESSION error
      if (error.message === 'INVALID_SESSION' || error.requiresReauth) {
        setConnectionStatus({
          type: 'error',
          message: (
            <>
              Salesforce authentication expired. 
              <a 
                href="/orgs" 
                className="underline ml-1 font-semibold"
              >
                Reconnect your org
              </a>
            </>
          )
        });
        // Don't throw the error, just show the message
      } else {
        setConnectionStatus({
          type: 'error',
          message: `Failed to sync flow interviews: ${error.message}`
        });
      }
    } finally {
      setIsSyncingInterviews(false);
    }
  };

  // Check for Salesforce connection status on component mount
  useEffect(() => {
    // Check the URL for SF auth flags
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('sfconnected') === 'true') {
      setConnectionStatus({
        type: 'success',
        message: 'Salesforce org connected successfully!'
      })
      
      // Remove the parameter from URL without page refresh
      const newUrl = window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    } else if (searchParams.get('sfpartial') === 'true') {
      setConnectionStatus({
        type: 'success',
        message: 'Salesforce authentication completed! Database update pending migration.'
      })
      
      // Remove the parameter from URL without page refresh
      const newUrl = window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    } else if (searchParams.get('sferror')) {
      setConnectionStatus({
        type: 'error',
        message: `Failed to connect Salesforce org: ${searchParams.get('sferror')}`
      })
      
      // Remove the parameter from URL without page refresh
      const newUrl = window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    } else if (salesforceError) {
      // Show any errors from the Salesforce connection attempt
      setConnectionStatus({
        type: 'error',
        message: `Error with Salesforce connection: ${salesforceError.message}`
      })
    }
  }, [orgs, salesforceError])

  // Mark Quick Start step 2 complete when flows are synced
  useEffect(() => {
    if (metrics && metrics.totalFlows > 0 && !stepsCompleted.firstSyncDone) {
      markStepComplete('firstSyncDone');
    }
  }, [metrics, stepsCompleted.firstSyncDone, markStepComplete]);

  const handleSignOut = async () => {
    setIsLoading(true)
    try {
      await signOut()
      // Navigation will happen automatically via auth state change
      navigate('/login')
    } catch (error) {
      // Sign out errors are handled by auth context
      // Even on error, redirect to login
      navigate('/login')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleImpactedUsersClick = () => {
    // Use imperative handle to expand the component
    mostAffectedUsersComponentRef.current?.expand();
    setTimeout(() => {
      const element = mostAffectedUsersRef.current;
      if (element) {
        const navbarHeight = 64; // Approximate navbar height
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight - 20; // Extra 20px for visual spacing
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleCriticalIssuesClick = () => {
    setExpandedWidget('critical-issues');
    setTimeout(() => {
      const element = document.getElementById('critical-issues');
      if (element) {
        const navbarHeight = 64; // Approximate navbar height
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight - 20; // Extra 20px for visual spacing
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  
  // If checking auth or loading orgs, show a spinner
  if (isCheckingAuth || isLoadingOrgs) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-lg text-gray-600">
          {isCheckingAuth ? 'Verifying authentication...' : 'Loading Salesforce organizations...'}
        </p>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onSignOut={handleSignOut} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with org selector on same line */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Flow Health Dashboard</h1>

            {/* Org selector on the right */}
            <div className="flex items-center space-x-3">
              <OrgSwitcher currentOrgId={selectedOrgId} />
              {/* Temporary User Sync Button for testing - COMMENTED OUT */}
              {/* <button
                onClick={async () => {
                  if (!selectedOrgId) {
                    setConnectionStatus({
                      type: 'error',
                      message: 'Please select an organization first'
                    });
                    return;
                  }
                  
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    
                    const response = await fetch('/api/salesforce/sync-users', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': session ? `Bearer ${session.access_token}` : ''
                      },
                      body: JSON.stringify({
                        orgId: selectedOrgId
                      })
                    });
                    
                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.message || 'Failed to sync users');
                    }
                    
                    const result = await response.json();
                    setConnectionStatus({
                      type: 'success',
                      message: `Synced ${result.synced} users successfully`
                    });
                    
                    // Refresh dashboard data
                    await fetchDashboardData();
                  } catch (error) {
                    setConnectionStatus({
                      type: 'error',
                      message: error instanceof Error ? error.message : 'Failed to sync users'
                    });
                  }
                }}
                disabled={!selectedOrgId}
                className="inline-flex items-center px-3 py-2 border border-dashed border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sync Users
              </button> */}
              
              {/* Test Error Sync Button - REMOVED after flow interview sync is working
              <button
                onClick={async () => {
                  if (!selectedOrgId) {
                    setConnectionStatus({
                      type: 'error',
                      message: 'Please select an organization first'
                    });
                    return;
                  }
                  
                  setIsSyncingInterviews(true);
                  
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    
                    // Call sync-flow-interviews endpoint
                    const syncResponse = await fetch('/api/salesforce/sync-flow-interviews', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': session ? `Bearer ${session.access_token}` : ''
                      },
                      body: JSON.stringify({
                        orgId: selectedOrgId
                      })
                    });
                    
                    if (!syncResponse.ok) {
                      const errorData = await syncResponse.json();
                      throw new Error(errorData.message || 'Failed to sync flow interviews');
                    }
                    
                    const syncResult = await syncResponse.json();
                    
                    // Log full response for debugging
                    console.log('Flow Interview Sync Result:', syncResult);
                    
                    // Show detailed message
                    if (syncResult.details) {
                      const details = syncResult.details;
                      setConnectionStatus({
                        type: syncResult.synced > 0 ? 'success' : 'none',
                        message: (
                          <div>
                            <p className="font-medium">{syncResult.message}</p>
                            {details.dateRange && (
                              <p className="text-sm mt-1">
                                Date range: {new Date(details.dateRange.from).toLocaleDateString()} to {new Date(details.dateRange.to).toLocaleDateString()}
                              </p>
                            )}
                            {details.errorTypes && Object.keys(details.errorTypes).length > 0 && (
                              <p className="text-xs mt-1">
                                Error types: {Object.entries(details.errorTypes).map(([type, count]) => `${type}: ${count}`).join(', ')}
                              </p>
                            )}
                          </div>
                        )
                      });
                    } else {
                      // Fallback message
                      setConnectionStatus({
                        type: syncResult.synced > 0 ? 'success' : 'none',
                        message: `${syncResult.message || `Successfully synced ${syncResult.synced || 0} flow interview errors`}`
                      });
                    }
                    
                    // Refresh dashboard data to show updated metrics
                    await fetchDashboardData();
                    
                  } catch (error) {
                    setConnectionStatus({
                      type: 'error',
                      message: error instanceof Error ? error.message : 'Failed to sync flow interviews'
                    });
                  } finally {
                    setIsSyncingInterviews(false);
                  }
                }}
                disabled={isSyncingInterviews || !selectedOrgId}
                className="inline-flex items-center px-3 py-2 border border-dashed border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-600 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSyncingInterviews ? (
                  <>
                    <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Syncing...
                  </>
                ) : (
                  <>
                    <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Test Error Sync
                  </>
                )}
              </button>
              */}
              
              {/* Org selector hidden per user request - functionality remains in navbar */}
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <main>
          {/* New User Welcome Banner */}
          {selectedOrgId && metrics && metrics.totalFlows === 0 && !localStorage.getItem('dismissedQuickStartBanner') && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <svg className="h-12 w-12 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Welcome to FlowDocs! 🎉
                    </h3>
                    <p className="text-gray-700 mb-3">
                      You're all set up! Follow our Quick Start Guide to connect your Salesforce org and sync your first flows in just a few minutes.
                    </p>
                    <button
                      onClick={() => navigate(`${selectedOrgId ? `/${selectedOrgId}` : ''}/help/quick-start`)}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors shadow-sm"
                    >
                      <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      </svg>
                      Start Quick Guide
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => {
                    localStorage.setItem('dismissedQuickStartBanner', 'true');
                    setConnectionStatus({type: 'none', message: ''});
                    window.location.reload(); // Refresh to hide banner
                  }}
                  className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Dismiss banner"
                >
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Connection Status Alert */}
          {connectionStatus.type !== 'none' && (
            <div className={`rounded-md p-4 mb-6 ${
              connectionStatus.type === 'success' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {connectionStatus.type === 'success' ? (
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    connectionStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {connectionStatus.message}
                  </p>
                </div>
                <div className="ml-auto pl-3">
                  <div className="-mx-1.5 -my-1.5">
                    <button 
                      onClick={() => setConnectionStatus({type: 'none', message: ''})} 
                      className={`inline-flex rounded-md p-1.5 ${
                        connectionStatus.type === 'success' 
                          ? 'bg-green-50 text-green-500 hover:bg-green-100' 
                          : 'bg-red-50 text-red-500 hover:bg-red-100'
                      }`}
                    >
                      <span className="sr-only">Dismiss</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Content */}
          {salesforceError ? (
            <div className="border-4 border-red-200 rounded-lg p-8">
              <div className="text-center">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Salesforce Integration Error</h3>
                <div className="mt-2 max-w-xl text-sm text-red-500 mx-auto">
                  <p>We encountered an error retrieving your Salesforce organizations.</p>
                  <p className="mt-1 font-mono text-xs bg-gray-100 p-2 rounded">
                    {salesforceError.message}
                  </p>
                </div>
                
                {salesforceError.message?.includes('infinite recursion') && (
                  <div className="mt-3 p-4 bg-yellow-50 rounded-lg text-left">
                    <p className="text-sm font-medium text-yellow-800">Database RLS Policy Error</p>
                    <ul className="mt-2 text-xs text-yellow-700 list-disc pl-5">
                      <li>This is a known database configuration issue</li>
                      <li>The salesforce_orgs table has a recursive RLS policy</li>
                      <li>Please disable RLS for this table by running the SQL in docs/SALESFORCE_ORGS_RLS_FIX.sql</li>
                      <li>This follows the same pattern already used for accounts and user_account_roles tables</li>
                    </ul>
                  </div>
                )}
                
                <div className="mt-5">
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Retry
                  </button>
                </div>
                <div className="mt-4 text-xs text-gray-500">
                  If this error persists, please contact support or try connecting your Salesforce org again.
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Show message if no org selected */}
              {!selectedOrgId && orgs.length > 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                  <p className="text-blue-800">Please select an organization from the dropdown above to view dashboard metrics.</p>
                </div>
              ) : !selectedOrgId && orgs.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-gray-600 mb-4">No organizations connected yet.</p>
                  {(userRole === 'Super-Admin' || userRole === 'Admin') ? (
                    <button
                      onClick={() => navigate('/connect-salesforce')}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Connect your first Salesforce Org
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">Contact your administrator to connect a Salesforce org.</p>
                  )}
                </div>
              ) : dashboardLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="ml-4 text-gray-600">Loading dashboard data...</p>
                </div>
              ) : dashboardError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">{dashboardError}</p>
                </div>
              ) : metrics ? (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <KeyMetricsCards 
                    metrics={metrics}
                    selectedOrgId={selectedOrgId}
                    affectedUsersCount={affectedUsers?.length || 0}
                    onImpactedUsersClick={handleImpactedUsersClick}
                    onCriticalIssuesClick={handleCriticalIssuesClick}
                  />

                  {/* Second Row - Issues Over Time and Issue Distribution */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Burn-up Chart - COMMENTED OUT FOR FUTURE USE
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Issues Fixed Over Time</h3>
                      {burnUpData && burnUpData.chartData.length > 0 ? (
                        <div>
                          <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={burnUpData.chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="date" 
                                tickFormatter={formatDate}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip 
                                formatter={(value) => [`${value} issues`, 'Fixed']}
                                labelFormatter={(label) => `Date: ${formatDate(label)}`}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="cumulativeFixed" 
                                stroke="#10B981" 
                                strokeWidth={2}
                                dot={{ fill: '#10B981', r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-500">This Week</p>
                              <p className="text-lg font-semibold text-gray-900">
                                {burnUpData.summary.thisWeek}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">This Month</p>
                              <p className="text-lg font-semibold text-gray-900">
                                {burnUpData.summary.thisMonth}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">All Time</p>
                              <p className="text-lg font-semibold text-gray-900">
                                {burnUpData.summary.allTime}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No historical data available yet</p>
                        </div>
                      )}
                    </div>
                    */}

                    {/* Issues Over Time - Burn Down Chart */}
                    <BurnDownChart 
                      burnUpData={burnUpData}
                      metrics={metrics}
                      selectedOrgId={selectedOrgId}
                    />

                    {/* Issue Distribution */}
                    <IssueDistributionChart 
                      issueDistribution={issueDistribution}
                      selectedOrgId={selectedOrgId}
                    />
                  </div>

                  {/* Third Row - Hidden Charts (Issues Fixed and Issue Trends) */}
                  <div className="hidden grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Issues Fixed - Burn Up Chart */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Issues Fixed (Cumulative)</h3>
                      {(() => {
                        // Show true cumulative issues fixed (always goes up)
                        if (!burnUpData || !burnUpData.chartData || burnUpData.chartData.length === 0) {
                          return (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                              <p>No issue fix data available yet</p>
                            </div>
                          );
                        }
                        
                        return (
                          <div>
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={burnUpData.chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis 
                                  dataKey="date" 
                                  tickFormatter={formatDate}
                                  tick={{ fontSize: 12 }}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip 
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
                                          <p className="font-medium text-sm">{formatDate(label)}</p>
                                          <p className="text-sm text-green-600">Total Fixed: {payload[0].value}</p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="cumulativeFixed" 
                                  stroke="#10B981" 
                                  strokeWidth={2}
                                  dot={{ fill: '#10B981', r: 4 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                            <div className="mt-4 text-center">
                              <p className="text-sm text-gray-500">Total Issues Fixed All Time</p>
                              <p className="text-2xl font-semibold text-green-600">
                                {burnUpData.chartData[burnUpData.chartData.length - 1]?.cumulativeFixed || 0}
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Issue Trends - Stacked Column Chart */}
                    <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Issue Trends (Last 2 Weeks)</h3>
                    {(() => {
                      if (!issueHistory || !issueHistory.chartData || issueHistory.chartData.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-64 text-gray-500">
                            <p>No historical issue data available</p>
                          </div>
                        );
                      }

                      const { chartData, issueTypes } = issueHistory;
                      
                      // Define colors for each issue type
                      const issueColors: { [key: string]: string } = {};
                      const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
                      issueTypes.forEach((issue: any, index: number) => {
                        issueColors[issue.type] = colors[index % colors.length];
                      });

                      return (
                        <div>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={chartData}
                              style={{ cursor: 'pointer' }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="date" 
                                tickFormatter={formatDate}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip 
                                content={({ active, payload, label }) => {
                                  if (active && payload && payload.length) {
                                    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                                    return (
                                      <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
                                        <p className="font-medium text-sm mb-2">{formatDate(label)}</p>
                                        <p className="text-sm font-medium mb-1">Total Issues: {total}</p>
                                        <div className="space-y-1">
                                          {payload
                                            .filter(entry => entry.value > 0)
                                            .sort((a, b) => (b.value || 0) - (a.value || 0))
                                            .map((entry, index) => {
                                              const issueType = issueTypes.find((t: any) => t.type === entry.dataKey);
                                              return (
                                                <div key={index} className="flex items-center justify-between text-xs">
                                                  <div className="flex items-center">
                                                    <div 
                                                      className="w-3 h-3 rounded mr-2" 
                                                      style={{ backgroundColor: entry.color }}
                                                    />
                                                    <span>{issueType?.displayName || entry.dataKey}</span>
                                                  </div>
                                                  <span className="font-medium ml-4">{entry.value}</span>
                                                </div>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              {issueTypes.map((issue: any) => (
                                <Bar 
                                  key={issue.type}
                                  dataKey={issue.type} 
                                  stackId="issues" 
                                  fill={issueColors[issue.type]}
                                  name={issue.displayName}
                                  onClick={(data: any) => {
                                    if (data && data.payload) {
                                      const clickedDate = data.payload.date;
                                      navigate(`/${selectedOrgId}/flows?issueType=${encodeURIComponent(issue.type)}&date=${encodeURIComponent(clickedDate)}&context=issueTrend`);
                                    }
                                  }}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                          
                          {/* Legend */}
                          <div className="mt-4 flex flex-wrap gap-3 justify-center">
                            {issueTypes.map((issue: any) => (
                              <div key={issue.type} className="flex items-center">
                                <div 
                                  className="w-3 h-3 rounded mr-2" 
                                  style={{ backgroundColor: issueColors[issue.type] }}
                                />
                                <span className="text-sm text-gray-600">{issue.displayName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    </div>
                  </div>

                  {/* Quick Wins Section */}
                  <QuickWinsWidget 
                    quickWins={quickWins}
                    selectedOrgId={selectedOrgId}
                  />

                  {/* Critical Issues Section */}
                  <CriticalIssuesWidget 
                    criticalIssues={criticalIssues}
                    selectedOrgId={selectedOrgId}
                    forceExpanded={expandedWidget === 'critical-issues'}
                  />

                  {/* Flow Interview Errors Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Daily Error Trend */}
                    <FlowErrorTrendChart flowErrorData={flowErrorData} />

                    {/* Error Type Distribution */}
                    <ErrorTypeDistribution 
                      errorDistribution={errorDistribution}
                      selectedOrgId={selectedOrgId}
                      onViewFlows={() => {
                        // This would scroll to FlowsByErrorType widget when enabled
                        // setTimeout(() => {
                        //   document.getElementById('error-flows-section')?.scrollIntoView({ behavior: 'smooth' });
                        // }, 100);
                      }}
                    />
                  </div>

                  {/* Error Types List */}
                  <ErrorTypesList
                    orgId={selectedOrgId}
                    className="mb-6"
                  />

                  {/* Affected Users */}
                  <div ref={mostAffectedUsersRef}>
                    <MostAffectedUsersEnhanced 
                      ref={mostAffectedUsersComponentRef}
                      orgId={selectedOrgId || ''}
                      onFlowClick={(flowId) => navigate(`/${selectedOrgId}/flow/${flowId}`)}
                      initialExpanded={shouldExpandAffectedUsers}
                    />
                  </div>


                  {/* Error-Related Flows Section - TODO: Complete implementation */}
                  {/* <FlowsByErrorType errorDistribution={errorDistribution} /> */}

                  {/* Last Sync Info */}
                  <div className="text-center text-sm text-gray-500">
                    Last updated: {new Date(metrics.lastSync).toLocaleString()}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </main>
      </div>
      
      {/* Chatbot */}
      <Chatbot />
    </div>
  )
}